import React, { useEffect, useState } from "react";
import { Image, Paper, Text } from "@mantine/core";
import QRCode from "qrcode";

interface SeminarCertificateProps {
  name: string;
  eventName: string;
  completionDate: string;
  country: string;
  certificateNumber: string;
}

// const formatDate = (dateStr: string) => {
//   if (!dateStr) return "";
//   const [year, month, day] = dateStr.split("-");
//   if (year && month && day) return `${month}-${day}-${year}`;
//   // already formatted MM-DD-YYYY
//   return dateStr;
// };
const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    // Input is YYYY-MM-DD → output DD-MM-YYYY
    const [year, month, day] = parts;
    return `${day}-${month}-${year}`;
  }
  // Already formatted, convert MM-DD-YYYY → DD-MM-YYYY
  const [month, day, year] = parts;
  return `${day}-${month}-${year}`;
};

const SeminarCertificate: React.FC<SeminarCertificateProps> = ({
  name,
  eventName,
  completionDate,
  country,
  certificateNumber,
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
  const seminarDisplayName = name?.trim() ? name : "\u00A0";

  const generateQRCode = async (certNum: string): Promise<string> => {
    try {
      const verificationUrl = `https://thinkcloudly.com/cert-verification?cert=${certNum}`;
      return await QRCode.toDataURL(verificationUrl, {
        width: 100,
        margin: 1,
        color: { dark: "#000000", light: "#FFFFFF" },
      });
    } catch {
      return "";
    }
  };

  useEffect(() => {
    if (certificateNumber) {
      generateQRCode(certificateNumber).then(setQrCodeDataUrl);
    }
  }, [certificateNumber]);

  const formattedDate = formatDate(completionDate);

  return (
    <Paper style={{ width: "210mm", height: "147mm", position: "relative" }}>
      {/* Background — same template as Bootcamp/Webinar */}
      <Image
        src="https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627"
        alt="Certificate Background"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />

      {/* Main Content */}

      <div
        style={{
          color: "#221E45",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          zIndex: 1,
          width: "100%",
          gap: "0px",
        }}
      >
        {/* Line 1 — small header */}
        <Text
          style={{
            fontSize: "13px",
            letterSpacing: "1.5px",
            fontFamily: "Garamond, Georgia, serif",
            textTransform: "uppercase",
            color: "#221E45",
            marginBottom: "6px",
          }}
        >
          This Certificate Is Proudly Presented To
        </Text>

        {/* Line 2 — recipient name, largest */}
        <Text
          style={{
            fontSize: "28px",
            fontWeight: "bold",
            fontFamily: "Georgia, serif",
            color: "#221E45",
            marginBottom: "10px",
            letterSpacing: "0.5px",
            minHeight: "42px",
            lineHeight: 1.2,
          }}
        >
          {seminarDisplayName}
        </Text>

        {/* Decorative divider */}
        <div
          style={{
            width: "60px",
            height: "2px",
            backgroundColor: "#E8634A",
            marginBottom: "10px",
            borderRadius: "2px",
          }}
        />

        {/* Lines 3-5 — grouped as a paragraph block */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "3px",
            lineHeight: "1.6",
          }}
        >
          <Text
            style={{
              fontSize: "12px",
              letterSpacing: "1px",
              fontFamily: "Garamond, Georgia, serif",
              textTransform: "uppercase",
              color: "#221E45",
            }}
          >
            For Participating In The
          </Text>

          <Text
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              fontFamily: "Garamond, Georgia, serif",
              textTransform: "uppercase",
              color: "#221E45",
              letterSpacing: "0.5px",
            }}
          >
            {eventName}
          </Text>

          <Text
            style={{
              fontSize: "12px",
              fontFamily: "Garamond, Georgia, serif",
              textTransform: "uppercase",
              color: "#555",
              letterSpacing: "0.5px",
            }}
          >
            {`Held On ${formattedDate}${country ? ` In ${country}` : ""}.`}
          </Text>
        </div>
      </div>
      {/* QR Code */}
      {qrCodeDataUrl && certificateNumber && (
        <div
          style={{
            position: "absolute",
            bottom: "55px",
            left: "75px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2,
          }}
        >
          <img
            src={qrCodeDataUrl}
            alt="QR Code"
            style={{ width: "90px", height: "9px", border: "1px solid #ccc" }}
          />
        </div>
      )}

      {/* Certificate Number & Issue Date */}
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "28px",
          display: "flex",
          flexDirection: "column",
          zIndex: 2,
        }}
      >
        {formattedDate && (
          <Text
            style={{
              fontSize: "10px",
              whiteSpace: "nowrap",
              fontWeight: "normal",
              marginLeft: "40px",
              color: "rgb(51, 51, 51)",
            }}
          >
            Issued On: {formattedDate}
          </Text>
        )}
        {certificateNumber && (
          <Text
            style={{
              fontSize: "11px",
              fontWeight: 400,
              color: "rgb(51, 51, 51)",
            }}
          >
            Certificate No: {certificateNumber}
          </Text>
        )}
      </div>
    </Paper>
  );
};

export default SeminarCertificate;
